import { registerRoute } from '../router';
import { apiFetch, ApiError } from '../api';
import { getUser } from '../state';
import { escapeHtml } from '../format';
import { openModal } from '../components/modal';
import { parseCsv } from '../csv';

interface Contact {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  tags: string[];
  notes: string | null;
  createdAt: string;
}

interface ImportResult {
  createdCount: number;
  skippedCount: number;
  errors: { row: number; message: string }[];
}

let contacts: Contact[] = [];
let searchDebounce: ReturnType<typeof setTimeout> | undefined;

function canManage(): boolean {
  const role = getUser()?.role;
  return role === 'administrator' || role === 'manager';
}

async function loadContacts(container: HTMLElement, search?: string): Promise<void> {
  const query = search ? `?search=${encodeURIComponent(search)}` : '';
  contacts = await apiFetch<Contact[]>(`/api/contacts${query}`);
  renderTable(container);
}

function renderTags(tags: string[]): string {
  if (tags.length === 0) return '';
  return tags.map((tag) => `<span class="badge badge--orange">${escapeHtml(tag)}</span>`).join(' ');
}

function renderTable(container: HTMLElement): void {
  const listEl = container.querySelector('#contacts-list')!;
  const manage = canManage();

  if (contacts.length === 0) {
    listEl.innerHTML = `<p style="color:var(--clr-text-muted);">No contacts yet.</p>`;
    return;
  }

  listEl.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:10px;">
      ${contacts
        .map(
          (contact) => `
        <div class="card" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;">
          <div style="min-width:0;">
            <div style="font-weight:600;">${escapeHtml(contact.name)}</div>
            <div style="color:var(--clr-text-muted);font-size:0.85rem;display:flex;gap:14px;flex-wrap:wrap;margin-top:2px;">
              ${contact.phone ? `<span>${escapeHtml(contact.phone)}</span>` : ''}
              ${contact.email ? `<span>${escapeHtml(contact.email)}</span>` : ''}
            </div>
            ${contact.tags.length > 0 ? `<div style="margin-top:8px;display:flex;gap:6px;flex-wrap:wrap;">${renderTags(contact.tags)}</div>` : ''}
            ${contact.notes ? `<p style="color:var(--clr-text-dim);font-size:0.82rem;margin-top:8px;">${escapeHtml(contact.notes)}</p>` : ''}
          </div>
          ${
            manage
              ? `<div style="display:flex;gap:8px;flex-shrink:0;">
                   <button class="btn btn--ghost" data-action="edit" data-id="${contact.id}">Edit</button>
                   <button class="btn btn--ghost" data-action="delete" data-id="${contact.id}">Delete</button>
                 </div>`
              : ''
          }
        </div>`,
        )
        .join('')}
    </div>
  `;

  if (!manage) return;

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const contact = contacts.find((c) => c.id === btn.dataset.id);
      if (contact) openContactModal(container, contact);
    });
  });

  listEl.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this contact?')) return;
      await apiFetch(`/api/contacts/${btn.dataset.id}`, { method: 'DELETE' });
      await loadContacts(container, (container.querySelector('#contacts-search') as HTMLInputElement)?.value);
    });
  });
}

function openContactModal(container: HTMLElement, existing?: Contact): void {
  const isEdit = Boolean(existing);

  const { root, close } = openModal(
    isEdit ? 'Edit contact' : 'Add contact',
    `
    <form id="contact-form">
      <div class="field">
        <label for="contact-name">Name</label>
        <input class="input" id="contact-name" required value="${existing ? escapeHtml(existing.name) : ''}">
      </div>
      <div class="field">
        <label for="contact-phone">Phone</label>
        <input class="input" id="contact-phone" placeholder="+15551234567" value="${existing?.phone ? escapeHtml(existing.phone) : ''}">
      </div>
      <div class="field">
        <label for="contact-email">Email</label>
        <input class="input" id="contact-email" type="email" value="${existing?.email ? escapeHtml(existing.email) : ''}">
      </div>
      <div class="field">
        <label for="contact-tags">Tags (comma separated)</label>
        <input class="input" id="contact-tags" placeholder="vip, lead" value="${existing ? escapeHtml(existing.tags.join(', ')) : ''}">
      </div>
      <div class="field">
        <label for="contact-notes">Notes</label>
        <textarea class="input" id="contact-notes">${existing?.notes ? escapeHtml(existing.notes) : ''}</textarea>
      </div>
      <p class="error-text" id="contact-error"></p>
      <div style="display:flex;gap:10px;justify-content:flex-end;">
        <button type="button" class="btn btn--secondary" id="contact-cancel">Cancel</button>
        <button type="submit" class="btn btn--primary">${isEdit ? 'Save changes' : 'Add contact'}</button>
      </div>
    </form>
  `,
  );

  root.querySelector('#contact-cancel')?.addEventListener('click', close);

  root.querySelector('#contact-form')?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const errorEl = root.querySelector('#contact-error')!;
    errorEl.textContent = '';

    const name = (root.querySelector('#contact-name') as HTMLInputElement).value.trim();
    const phone = (root.querySelector('#contact-phone') as HTMLInputElement).value.trim();
    const email = (root.querySelector('#contact-email') as HTMLInputElement).value.trim();
    const tags = (root.querySelector('#contact-tags') as HTMLInputElement).value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const notes = (root.querySelector('#contact-notes') as HTMLTextAreaElement).value.trim();

    const payload = { name, phone: phone || null, email: email || null, tags, notes: notes || null };

    try {
      if (isEdit && existing) {
        await apiFetch(`/api/contacts/${existing.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
      } else {
        await apiFetch('/api/contacts', { method: 'POST', body: JSON.stringify(payload) });
      }
      close();
      await loadContacts(container, (container.querySelector('#contacts-search') as HTMLInputElement)?.value);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

function openImportModal(container: HTMLElement): void {
  const { root, close } = openModal(
    'Import contacts from CSV',
    `
    <p style="color:var(--clr-text-muted);font-size:0.85rem;margin-bottom:14px;">
      Columns: <code>name</code> (required), <code>phone</code>, <code>email</code>, <code>tags</code>
      (semicolon-separated), <code>notes</code>. At least one of phone/email is required per row.
    </p>
    <div class="field">
      <input class="input" id="import-file" type="file" accept=".csv,text/csv">
    </div>
    <div id="import-preview" style="font-size:0.85rem;color:var(--clr-text-muted);margin-bottom:14px;"></div>
    <p class="error-text" id="import-error"></p>
    <div style="display:flex;gap:10px;justify-content:flex-end;">
      <button type="button" class="btn btn--secondary" id="import-cancel">Cancel</button>
      <button type="button" class="btn btn--primary" id="import-submit" disabled>Import</button>
    </div>
  `,
  );

  let parsedRows: Record<string, string>[] = [];
  const preview = root.querySelector('#import-preview')!;
  const submitBtn = root.querySelector<HTMLButtonElement>('#import-submit')!;

  root.querySelector('#import-file')?.addEventListener('change', async (event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const text = await file.text();
    parsedRows = parseCsv(text);
    preview.textContent = `${parsedRows.length} row(s) found.`;
    submitBtn.disabled = parsedRows.length === 0;
  });

  root.querySelector('#import-cancel')?.addEventListener('click', close);

  submitBtn.addEventListener('click', async () => {
    const errorEl = root.querySelector('#import-error')!;
    errorEl.textContent = '';

    const payload = {
      contacts: parsedRows.map((row) => ({
        name: row.name ?? '',
        phone: row.phone || null,
        email: row.email || null,
        tags: (row.tags ?? '')
          .split(';')
          .map((t) => t.trim())
          .filter(Boolean),
        notes: row.notes || null,
      })),
    };

    try {
      const result = await apiFetch<ImportResult>('/api/contacts/import', { method: 'POST', body: JSON.stringify(payload) });
      preview.innerHTML = `
        Imported ${result.createdCount}, skipped ${result.skippedCount}.
        ${result.errors.length > 0 ? `<ul style="margin-top:8px;">${result.errors.map((e) => `<li>Row ${e.row}: ${escapeHtml(e.message)}</li>`).join('')}</ul>` : ''}
      `;
      submitBtn.disabled = true;
      await loadContacts(container, (container.querySelector('#contacts-search') as HTMLInputElement)?.value);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : 'Something went wrong.';
    }
  });
}

registerRoute({
  path: '/contacts',
  title: 'Contacts',
  render: ({ container }) => {
    const manage = canManage();

    container.innerHTML = `
      <div class="page-header" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;">
        <div>
          <h1>Contacts</h1>
          <p>Your recipient list — searchable, taggable, ready for campaigns.</p>
        </div>
        ${
          manage
            ? `<div style="display:flex;gap:10px;">
                 <button class="btn btn--secondary" id="import-contacts-btn">Import CSV</button>
                 <button class="btn btn--primary" id="add-contact-btn">+ Add contact</button>
               </div>`
            : ''
        }
      </div>
      <div class="field" style="max-width:320px;">
        <input class="input" id="contacts-search" placeholder="Search name, phone, email…">
      </div>
      <div id="contacts-list">Loading…</div>
    `;

    container.querySelector('#add-contact-btn')?.addEventListener('click', () => openContactModal(container));
    container.querySelector('#import-contacts-btn')?.addEventListener('click', () => openImportModal(container));

    container.querySelector('#contacts-search')?.addEventListener('input', (event) => {
      clearTimeout(searchDebounce);
      const value = (event.target as HTMLInputElement).value;
      searchDebounce = setTimeout(() => loadContacts(container, value), 250);
    });

    loadContacts(container).catch(() => {
      container.querySelector('#contacts-list')!.textContent = 'Could not load contacts.';
    });
  },
});
